import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeComponentFieldEnum } from 'src/app/domain/registration/enums/type-component-dynamic.enum';
import { CustomerService } from '../crud-service';
import { CustomerBranch } from '../customer-branch.util';
import { CustomerResponsible, emptyResponsible } from '../customer-responsible.util';

export interface CustomerResponsibleDialogData {
  responsible: CustomerResponsible | null;
  branches: CustomerBranch[];
  viewMode: boolean;
}

@Component({
  selector: 'app-customer-responsible-dialog',
  templateUrl: './customer-responsible-dialog.component.html',
  styleUrls: ['./customer-responsible-dialog.component.sass'],
})
export class CustomerResponsibleDialogComponent {
  readonly viewMode: boolean;
  readonly branches: CustomerBranch[];
  readonly fields: IFieldConfig[] = [
    { name: 'responsibleName', label: 'NOME', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleCpf', label: 'CPF', type: TypeComponentFieldEnum.INPUT_CPF },
    { name: 'responsibleRg', label: 'RG', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleNationality', label: 'NACIONALIDADE', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsiblePhone', label: 'CELULAR', type: TypeComponentFieldEnum.INPUT_CELLPHONE },
    { name: 'responsibleEmail', label: 'E-MAIL', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleBirthDate', label: 'NASCIMENTO', type: TypeComponentFieldEnum.INPUT_DATE },
    { name: 'responsibleZipCode', label: 'CEP', type: TypeComponentFieldEnum.INPUT_CEP },
    { name: 'responsibleAddress', label: 'ENDEREÇO', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleNumber', label: 'NÚMERO', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleNeighborhood', label: 'BAIRRO', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleCity', label: 'CIDADE', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleState', label: 'UF', type: TypeComponentFieldEnum.INPUT },
    { name: 'responsibleComplement', label: 'COMPLEMENTO', type: TypeComponentFieldEnum.INPUT },
  ];

  form = this.fb.group({
    enterpriseKey: [''],
    responsibleName: ['', Validators.required],
    responsibleCpf: [''],
    responsibleRg: [''],
    responsibleNationality: ['Brasileiro'],
    responsiblePhone: [''],
    responsibleEmail: [''],
    responsibleBirthDate: [''],
    responsibleZipCode: [''],
    responsibleAddress: [''],
    responsibleNumber: [''],
    responsibleNeighborhood: [''],
    responsibleCity: [''],
    responsibleState: [''],
    responsibleComplement: [''],
  });

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private dialogRef: MatDialogRef<CustomerResponsibleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomerResponsibleDialogData
  ) {
    this.viewMode = !!data?.viewMode;
    this.branches = data?.branches || [];
    const responsible = data?.responsible;
    const selected = this.branches.find(
      (branch) =>
        (branch.id && branch.id === responsible?.enterpriseId) ||
        branch.localKey === responsible?.enterpriseId
    );
    this.form.patchValue({
      enterpriseKey: selected?.localKey || '',
      responsibleName: responsible?.responsibleName || '',
      responsibleCpf: responsible?.responsibleCpf || '',
      responsibleRg: responsible?.responsibleRg || '',
      responsibleNationality: responsible?.responsibleNationality || 'Brasileiro',
      responsiblePhone: responsible?.responsiblePhone || '',
      responsibleEmail: responsible?.responsibleEmail || '',
      responsibleBirthDate: responsible?.responsibleBirthDate || '',
      responsibleZipCode: responsible?.responsibleZipCode || '',
      responsibleAddress: responsible?.responsibleAddress || '',
      responsibleNumber: responsible?.responsibleNumber || '',
      responsibleNeighborhood: responsible?.responsibleNeighborhood || '',
      responsibleCity: responsible?.responsibleCity || '',
      responsibleState: responsible?.responsibleState || '',
      responsibleComplement: responsible?.responsibleComplement || '',
    });
    if (this.viewMode) this.form.disable({ emitEvent: false });
  }

  get fieldType(): TypeCesEnum {
    return this.viewMode ? TypeCesEnum.SHOW : TypeCesEnum.EDIT;
  }

  config(name: string): IFieldConfig {
    return this.fields.find((field) => field.name === name) || this.fields[0];
  }

  get title(): string {
    if (this.viewMode) return 'Visualizar responsável';
    return this.data?.responsible ? 'Editar responsável' : 'Cadastrar responsável';
  }

  lookupCep(): void {
    const cep = String(this.form.get('responsibleZipCode')?.value || '').replace(/\D/g, '');
    if (cep.length !== 8 || this.viewMode) return;
    this.customerService
      .getCepInfo(cep)
      .pipe(take(1))
      .subscribe({
        next: (result: any) => {
          this.form.patchValue({
            responsibleAddress: result?.address || this.form.get('responsibleAddress')?.value,
            responsibleNeighborhood: result?.neighborhood || this.form.get('responsibleNeighborhood')?.value,
            responsibleCity: result?.city || this.form.get('responsibleCity')?.value,
            responsibleState: result?.state || this.form.get('responsibleState')?.value,
          });
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const branch = this.branches.find((item) => item.localKey === value.enterpriseKey);
    const current = this.data?.responsible;
    const responsible = emptyResponsible({
      ...current,
      id: current?.id,
      draftId: current?.draftId || (!current?.id ? `responsible-${Date.now()}` : undefined),
      enterpriseId: branch?.id || branch?.localKey || null,
      branchTitle: branch?.branchTitle || '',
      responsibleName: String(value.responsibleName || ''),
      responsibleCpf: String(value.responsibleCpf || ''),
      responsibleRg: String(value.responsibleRg || ''),
      responsibleNationality: String(value.responsibleNationality || ''),
      responsiblePhone: String(value.responsiblePhone || ''),
      responsibleEmail: String(value.responsibleEmail || ''),
      responsibleBirthDate: String(value.responsibleBirthDate || ''),
      responsibleZipCode: String(value.responsibleZipCode || ''),
      responsibleAddress: String(value.responsibleAddress || ''),
      responsibleNumber: String(value.responsibleNumber || ''),
      responsibleNeighborhood: String(value.responsibleNeighborhood || ''),
      responsibleCity: String(value.responsibleCity || ''),
      responsibleState: String(value.responsibleState || ''),
      responsibleComplement: String(value.responsibleComplement || ''),
    });
    this.dialogRef.close(responsible);
  }
}
